import React, { useEffect, useState } from "react";
import { Grid, Card, TablePagination, Box, Typography, Chip } from "@mui/material";
import { PiFileMagnifyingGlassDuotone } from "react-icons/pi";
import { FcEndCall } from "react-icons/fc";
import moment from "moment-timezone"; // ✅ Import moment-timezone

import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import MDInput from "components/MDInput";
import DataTable from "examples/Tables/DataTable";
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import Loader from "../../../Loader/loader";

// ✅ Time difference calculator (same logic as your existing code)
const calculateTimeDifference = (startTime, currentTime) => {
  if (!startTime || !currentTime) return "00:00:00";

  const start = new Date(startTime);
  const current = new Date(currentTime);
  const diffMs = current - start;

  if (diffMs < 0) return "00:00:00";

  const hours = Math.floor(diffMs / (1000 * 60 * 60));
  const minutes = Math.floor((diffMs % (1000 * 60 * 60)) / (1000 * 60));
  const seconds = Math.floor((diffMs % (1000 * 60)) / 1000);

  return `${hours.toString().padStart(2, "0")}:${minutes.toString().padStart(2, "0")}:${seconds
    .toString()
    .padStart(2, "0")}`;
};

function LiveCallsTable() {
  const [rows, setRows] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [page, setPage] = useState(0);
  const [perPage, setPerPage] = useState(10);
  const [totalCount, setTotalCount] = useState(0);
  const [searchTerm, setSearchTerm] = useState("");
  const [liveCalls, setLiveCalls] = useState([]);
  const [currentTime, setCurrentTime] = useState(new Date()); // ✅ Real-time update

  const socket_url = process.env.REACT_APP_SOCKET_URL;

  const columns = [
    { Header: "caller number", accessor: "caller_number", align: "left" },
    { Header: "campaign name", accessor: "campaign_name", align: "left" },
    { Header: "destination", accessor: "destination", align: "center" },
    { Header: "Customer Number", accessor: "customer_number", align: "center" },
    { Header: "duration", accessor: "duration", align: "center" },
    { Header: "Call Status A", accessor: "call_status_A", align: "center" },
    { Header: "Call Status B", accessor: "call_status_B", align: "center" },
    { Header: "action", accessor: "action", align: "center" },
  ];

  // ✅ Timer: Har second duration update
  useEffect(() => {
    const timer = setInterval(() => {
      setCurrentTime(new Date());
    }, 1000);

    return () => clearInterval(timer);
  }, []);

  // WebSocket Connection
  useEffect(() => {
    let socket;

    try {
      socket = new WebSocket(socket_url);

      socket.onopen = () => {
        console.log("WebSocket Connected");
        socket.send(JSON.stringify({ action: "live_calls_data" }));
      };

      socket.onmessage = (event) => {
        const fetchData = JSON.parse(event.data);

        if (fetchData.type === "pong") return;

        if (fetchData.livecalls !== undefined) {
          setLiveCalls(fetchData.livecalls);
          setIsLoading(false);
        }
      };

      socket.onerror = (error) => {
        console.error("WebSocket Error:", error);
        setIsLoading(false);
      };

      socket.onclose = () => {
        console.log("WebSocket Disconnected");
        setIsLoading(false);
      };
    } catch (error) {
      console.error("Socket connection error:", error);
      setIsLoading(false);
    }

    return () => {
      if (socket) socket.close();
    };
  }, [socket_url]);

  // Process received live calls
  useEffect(() => {
    let allCalls = [];

    // ✅ Process liveCalls exactly like your existing code
    const processedCalls = liveCalls?.map((call) => {
      // ✅ Convert UTC to formatted string (exactly like your code)
      const created_at = call?.created_at
        ? moment.utc(call.created_at).format("YYYY-MM-DDTHH:mm:ss")
        : null;

      const start_at = call?.updated_at
        ? moment.utc(call.updated_at).format("YYYY-MM-DDTHH:mm:ss")
        : null;

      // ✅ Current time in America/New_York timezone
      const current_at = moment().tz("America/New_York").format("YYYY-MM-DDTHH:mm:ss");

      // ✅ Calculate duration exactly like your code
      const duration =
        call?.updated_at === null
          ? calculateTimeDifference(created_at, current_at)
          : calculateTimeDifference(start_at, current_at);

      return {
        ...call,
        created_at,
        updated_at: start_at,
        duration, // ✅ Add duration to call object
      };
    });

    allCalls = processedCalls || [];

    // Search
    if (searchTerm) {
      allCalls = allCalls.filter(
        (call) =>
          call?.caller_number?.toLowerCase().includes(searchTerm.toLowerCase()) ||
          call?.destination?.toLowerCase().includes(searchTerm.toLowerCase()) ||
          call?.campaign_name?.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }

    setTotalCount(allCalls.length);

    // Pagination
    const startIndex = page * perPage;
    const paginatedCalls = allCalls.slice(startIndex, startIndex + perPage);

    // Format Table Rows
    const formattedRows = paginatedCalls.map((call) => {
      const smallText = (text) => (
        <MDTypography fontSize="16px" lineHeight="18px">
          {text}
        </MDTypography>
      );

      return {
        caller_number: smallText(call.caller_number || "N/A"),
        campaign_name: smallText(call.campaign_name || "N/A"),
        destination: smallText(call.destination || "N/A"),
        customer_number: smallText(call.customer_number || "N/A"),
        duration: smallText(call.duration || "00:00:00"), // ✅ Use calculated duration
        call_status_A: (
          <Chip
            label={call.call_status_A === "1" ? "Answered" : "Ringing"}
            color={call.call_status_A === "1" ? "success" : "warning"}
            size="small"
            sx={{ fontSize: "11px", height: "22px" }}
          />
        ),
        call_status_B: (
          <Chip
            label={call.call_status_B === "1" ? "Answered" : "Ringing"}
            color={call.call_status_B === "1" ? "success" : "warning"}
            size="small"
            sx={{ fontSize: "11px", height: "22px" }}
          />
        ),
        action: (
          <Box display="flex" justifyContent="center">
            <FcEndCall
              size={18}
              style={{ cursor: "pointer" }}
              onClick={() => handleCallHangup(call)}
            />
          </Box>
        ),
      };
    });

    setRows(formattedRows);
  }, [liveCalls, searchTerm, page, perPage, currentTime]); // ✅ currentTime dependency

  const handleCallHangup = async (call) => {
    if (!window.confirm("Are you sure you want to hangup this call?")) return;
    console.log("Hanging up call:", call);
    alert("Call hangup request sent");
  };

  return (
    <DashboardLayout>
      <DashboardNavbar />

      <MDBox pt={3}>
        <Grid item xs={12}>
          <Card>
            <MDBox
              mx={2}
              mt={-3}
              py={3}
              px={2}
              variant="gradient"
              bgColor="info"
              borderRadius="lg"
              display="flex"
              justifyContent="space-between"
            >
              <MDTypography variant="h6" color="white">
                Live Calls Table
              </MDTypography>

              <MDInput
                placeholder="Search here..."
                value={searchTerm}
                onChange={(e) => {
                  setSearchTerm(e.target.value);
                  setPage(0);
                }}
                sx={{ backgroundColor: "#fff", minWidth: "250px" }}
              />
            </MDBox>

            <MDBox pt={3}>
              {isLoading ? (
                <Grid container justifyContent="center" sx={{ py: 5 }}>
                  <Loader />
                </Grid>
              ) : rows.length === 0 ? (
                <Box textAlign="center" sx={{ py: 5 }}>
                  <PiFileMagnifyingGlassDuotone size={50} color="#9e9e9e" />
                  <Typography variant="h6" sx={{ mt: 2 }}>
                    No Live Calls Found
                  </Typography>
                </Box>
              ) : (
                <>
                  <DataTable
                    table={{ columns, rows }}
                    isSorted={false}
                    entriesPerPage={false}
                    showTotalEntries={false}
                    noEndBorder
                  />

                  {/* <TablePagination
                    component="div"
                    count={totalCount}
                    page={page}
                    onPageChange={(e, newPage) => setPage(newPage)}
                    rowsPerPage={perPage}
                    onRowsPerPageChange={(e) => {
                      setPerPage(parseInt(e.target.value));
                      setPage(0);
                    }}
                    rowsPerPageOptions={[5, 10, 25, 50, 100]}
                  /> */}
                </>
              )}
            </MDBox>
          </Card>
        </Grid>
      </MDBox>
    </DashboardLayout>
  );
}

export default LiveCallsTable;
